import type { ReactNode } from 'react';

import { MantineProvider } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { getStyleNonce } from '@/app/cspNonce';
import { buildMantineShades } from '@/shared/color/mantineShades';
import { sessionQueryOptions } from '@/shared/session/session';

import { appearanceQueryOptions } from './appearance';

/**
 * Applies the tenant's `primaryColor`, when set, as the Mantine theme's own primary color (`13`
 * §6.5, `D10`: theme tokens, never a redesign). A nested `MantineProvider` only overrides
 * `colors.steel` - `theme.ts`'s `primaryColor` stays `'steel'`, so every `color="steel"` reference
 * across the console and the portal keeps working, now resolving to the tenant's shades. The
 * fixed `--sl-steel-*` custom properties of `tokens.css` (the platform's own brand mark, e.g.
 * `TenantMark`) are a separate, deliberately static layer this does not touch.
 *
 * Enabled only once a tenant is active: `/tenant-settings/appearance` takes `RequireTenant()`, and
 * the query is otherwise a 401 nobody needs to see before login.
 */
export function TenantThemeProvider({ children }: { children: ReactNode }) {
  const session = useQuery(sessionQueryOptions());
  const appearance = useQuery({
    ...appearanceQueryOptions(),
    enabled: session.data?.activeTenant !== null && session.data?.activeTenant !== undefined,
  });

  const shades =
    appearance.data?.primaryColor === null || appearance.data?.primaryColor === undefined
      ? null
      : buildMantineShades(appearance.data.primaryColor);

  if (shades === null) {
    return children;
  }

  return (
    <MantineProvider theme={{ colors: { steel: shades } }} getStyleNonce={getStyleNonce}>
      {children}
    </MantineProvider>
  );
}
